import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Email as EmailIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { authAPI, profileAPI } from '../services/api';
import ReCaptchaWidget from '../components/ReCaptchaWidget';

const YandexIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0z" fill="#FC3F1D" />
    <path d="M11.5 5h-2c-1.93 0-3 1.34-3 3.17 0 1.5.78 2.5 2.08 3.33L7 16h2.2l1.7-4.67h.1V16H15V5h-2v3.17c.6-.83 1-1.67 1-2.5C14 5 13 5 11.5 5z" fill="white" />
  </svg>
);

const LoginModal: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { loginModalOpen, setLoginModalOpen, isAuthenticated, user, acceptConsent } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'login' | 'register' | 'verify' | 'recover'>('login');
  const [consentChecked, setConsentChecked] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const organizerId = urlParams.get('organizer_id');
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  const handleOAuthLogin = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/yandex/callback`);
    const clientId = import.meta.env.VITE_YANDEX_CLIENT_ID;

    if (!clientId) {
      setError('OAuth Client ID для Яндекс не настроен.');
      return;
    }

    const authUrl = `https://oauth.yandex.ru/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=login:email%20login:info%20login:avatar`;

    if (organizerId) {
      sessionStorage.setItem('pending_organizer_tracking', JSON.stringify({
        organizer_id: Number(organizerId),
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
      }));
    }

    window.location.href = authUrl;
  };

  const handleClose = () => {
    if (isAuthenticated && user?.agreements_accepted) {
      setLoginModalOpen(false);
    } else if (!isAuthenticated) {
      setLoginModalOpen(false);
    }
    setError(null);
    setMode('login');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleContinueAsGuest = () => {
    setLoginModalOpen(false);
    if (window.location.pathname === '/login') {
      window.location.href = '/events';
    }
  };

  const handleAcceptConsent = async () => {
    if (!consentChecked) {
      setError('Необходимо дать согласие на обработку персональных данных');
      return;
    }
    await acceptConsent();
  };

  if (!loginModalOpen) return null;

  const showConsentPrompt = isAuthenticated && user && !user.agreements_accepted;

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1300,
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: isMobile ? '16px 16px 0 0' : 3,
          width: '100%',
          maxWidth: 420,
          maxHeight: isMobile ? '70vh' : '90vh',
          overflowY: 'auto',
          position: 'relative',
          ...(isMobile
            ? { pb: 'env(safe-area-inset-bottom, 0px)' }
            : { mx: 2 }),
          animation: isMobile ? 'slideUp 0.3s ease-out' : 'fadeIn 0.2s ease-out',
          '@keyframes slideUp': {
            from: { transform: 'translateY(100%)' },
            to: { transform: 'translateY(0)' },
          },
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'scale(0.95)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {showConsentPrompt ? 'Согласие на обработку данных' : 'Вход'}
            </Typography>
            {(!isAuthenticated || (isAuthenticated && user?.agreements_accepted)) && (
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {showConsentPrompt ? (
            <ConsentPrompt
              consentChecked={consentChecked}
              setConsentChecked={setConsentChecked}
              onAccept={handleAcceptConsent}
              error={error}
              setError={setError}
            />
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleContinueAsGuest}
                sx={{ mb: 2, py: 1.2, borderColor: 'divider', color: 'text.secondary' }}
              >
                Смотреть мероприятия без авторизации
              </Button>

              <Divider sx={{ mb: 2 }} />

              {error && (
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<YandexIcon />}
                onClick={handleOAuthLogin}
                sx={{
                  mb: 2,
                  py: 1.3,
                  bgcolor: '#FC3F1D',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#D63316' },
                }}
              >
                Войти через Яндекс
              </Button>

              <Divider sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">или</Typography>
              </Divider>

              <EmailAuthForm
                onError={setError}
                mode={mode}
                setMode={setMode}
              />

              <Divider sx={{ mt: 2, mb: 1 }} />

              {mode === 'login' && (
                <Button
                  fullWidth
                  variant="text"
                  size="large"
                  onClick={() => setMode('register')}
                  sx={{ py: 1 }}
                >
                  Регистрация
                </Button>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                Продолжая, вы принимаете условия использования сервиса
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ConsentPrompt: React.FC<{
  consentChecked: boolean;
  setConsentChecked: (v: boolean) => void;
  onAccept: () => void;
  error: string | null;
  setError: (v: string | null) => void;
}> = ({ consentChecked, setConsentChecked, onAccept, error, setError }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Просим вас ознакомиться с{' '}
      <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover">
        Политикой конфиденциальности
      </Link>{' '}
      и дать согласие на{' '}
      <Link href="/terms-of-service" target="_blank" rel="noopener noreferrer" underline="hover">
        обработку персональных данных
      </Link>
      . В противном случае авторизация невозможна.
    </Typography>

    {error && (
      <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }} onClose={() => setError(null)}>
        {error}
      </Alert>
    )}

    <FormControlLabel
      control={
        <Checkbox
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          color="primary"
          size="small"
        />
      }
      label={
        <Typography variant="body2" color="text.secondary">
          Я ознакомился(ась) с{' '}
          <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover">
            Политикой конфиденциальности
          </Link>{' '}
          и даю согласие на обработку персональных данных
        </Typography>
      }
      sx={{
        alignItems: 'flex-start',
        mb: 2,
        '& .MuiFormControlLabel-label': { lineHeight: 1.4 },
        '& .MuiCheckbox-root': { paddingTop: '4px' },
      }}
    />

    <Button
      fullWidth
      variant="contained"
      size="large"
      onClick={onAccept}
      disabled={!consentChecked}
    >
      Подтвердить
    </Button>
  </Box>
);

const EmailAuthForm: React.FC<{
  onError: (msg: string) => void;
  mode: 'login' | 'register' | 'verify' | 'recover';
  setMode: (mode: 'login' | 'register' | 'verify' | 'recover') => void;
}> = ({ onError, mode, setMode }) => {
  const { emailLogin, emailRegister } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [loginConsentRequired, setLoginConsentRequired] = useState(false);
  const [loginConsentChecked, setLoginConsentChecked] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [consentMessage, setConsentMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [regError, setRegError] = useState('');
  const [loginFailed, setLoginFailed] = useState(false);
  const [recoverStep, setRecoverStep] = useState<'email' | 'code'>('email');
  const [recoverMessage, setRecoverMessage] = useState('');

  const PASSWORD_MIN_LENGTH = 10;

  useEffect(() => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setVerificationCode('');
    setConsentGiven(false);
    setCaptchaRequired(false);
    setCaptchaToken(null);
    setCodeSent(false);
    setCodeVerified(false);
    setVerifyMessage('');
    setRegError('');
    setRecoverStep('email');
    setRecoverMessage('');
    onError('');
  }, [mode]);

  const passwordErrors: string[] = [];
  if ((mode === 'register' || mode === 'recover') && password) {
    if (password.length < PASSWORD_MIN_LENGTH) passwordErrors.push(`минимум ${PASSWORD_MIN_LENGTH} символов`);
    if (!/[A-ZА-ЯЁ]/.test(password)) passwordErrors.push('заглавная буква');
    if (!/[a-zа-яё]/.test(password)) passwordErrors.push('строчная буква');
    if (!/\d/.test(password)) passwordErrors.push('цифра');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) passwordErrors.push('спецсимвол');
  }

  const handleLogin = async (opts?: { consent_accepted?: boolean; captcha_token?: string }) => {
    if (!email || !password) {
      onError('Введите email и пароль');
      return;
    }
    setLoading(true);
    onError('');
    try {
      const response = await emailLogin(email, password, opts);
      if (!response) return;
      if (response.consent_required) {
        setLoginConsentRequired(true);
        setConsentMessage(response.message || '');
        setCaptchaRequired(false);
      } else if (response.captcha_required) {
        setCaptchaRequired(true);
        setCaptchaToken(null);
        setPassword('');
        setLoginConsentRequired(false);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Ошибка входа';
      onError(typeof msg === 'string' ? msg : 'Ошибка входа');
      setLoginFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginConsentAccept = () => {
    if (!loginConsentChecked) {
      onError('Необходимо дать согласие на обработку персональных данных');
      return;
    }
    setLoginConsentRequired(false);
    handleLogin({ consent_accepted: true });
  };

  const handleSendRegisterCode = async () => {
    setRegError('');
    if (!email) { setRegError('Введите email'); return; }
    if (!firstName.trim()) { setRegError('Введите имя'); return; }
    if (!lastName.trim()) { setRegError('Введите фамилию'); return; }
    if (!password) { setRegError('Введите пароль'); return; }
    if (passwordErrors.length > 0) { setRegError(`Пароль не соответствует требованиям: ${passwordErrors.join(', ')}`); return; }
    if (password !== confirmPassword) { setRegError('Пароли не совпадают'); return; }
    setLoading(true);
    try {
      await authAPI.sendVerificationCode(email);
      setCodeSent(true);
      setRegError('');
      setVerifyMessage(`На вашу почту ${email} отправлен код. Введите его и подтвердите корректность e-mail`);
    } catch (err: any) {
      setRegError(err.response?.data?.detail || 'Ошибка отправки кода');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async () => {
    setRegError('');
    if (!verificationCode) { setRegError('Введите код подтверждения'); return; }
    if (!consentGiven) { setRegError('Необходимо дать согласие на обработку персональных данных'); return; }
    setLoading(true);
    try {
      await emailRegister({
        email, password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        verification_code: verificationCode,
      });
      setCodeVerified(true);
      setVerifyMessage('');
    } catch (err: any) {
      setRegError(err.response?.data?.detail || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRecoverCode = async () => {
    setRegError('');
    if (!email) { setRegError('Введите email'); return; }
    setLoading(true);
    try {
      await authAPI.sendVerificationCode(email, 'password_change');
      setRecoverStep('code');
      setRecoverMessage(`На вашу почту ${email} отправлен код. Введите его и новый пароль`);
      setRegError('');
    } catch (err: any) {
      setRegError(err.response?.data?.detail || 'Ошибка отправки кода');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setRegError('');
    if (!verificationCode) { setRegError('Введите код подтверждения'); return; }
    if (!password) { setRegError('Введите новый пароль'); return; }
    if (passwordErrors.length > 0) { setRegError(`Пароль не соответствует требованиям: ${passwordErrors.join(', ')}`); return; }
    if (password !== confirmPassword) { setRegError('Пароли не совпадают'); return; }
    setLoading(true);
    try {
      await profileAPI.resetPassword({ email, new_password: password, verification_code: verificationCode });
      setRecoverMessage('Пароль успешно изменён. Войдите с новым паролем.');
      setRegError('');
      setTimeout(() => setMode('login'), 2000);
    } catch (err: any) {
      setRegError(err.response?.data?.detail || 'Ошибка восстановления пароля');
    } finally {
      setLoading(false);
    }
  };

  if (loginConsentRequired) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {consentMessage}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={loginConsentChecked}
              onChange={(e) => setLoginConsentChecked(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Я ознакомился(ась) с{' '}
              <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover">
                Политикой конфиденциальности
              </Link>{' '}
              и даю согласие на обработку персональных данных
            </Typography>
          }
          sx={{
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': { lineHeight: 1.4 },
            '& .MuiCheckbox-root': { paddingTop: '4px' },
          }}
        />
        <Button variant="contained" size="large" onClick={handleLoginConsentAccept} disabled={loading || !loginConsentChecked}>
          {loading ? 'Вход...' : 'Подтвердить и войти'}
        </Button>
        <Button variant="text" size="small" onClick={() => { setLoginConsentRequired(false); }}>
          Отмена
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {mode === 'verify' && verifyMessage && (
        <Alert severity="success" sx={{ textAlign: 'left' }}>{verifyMessage}</Alert>
      )}

      {mode !== 'verify' && mode !== 'recover' && (
        <>
          <TextField
            fullWidth
            label={mode === 'register' ? <span>Email <span style={{ color: 'red' }}>*</span></span> : 'Email'}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || codeSent}
            size="small"
          />
          {mode === 'register' && (
            <>
              <TextField
                fullWidth
                label={<span>Имя <span style={{ color: 'red' }}>*</span></span>}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading || codeSent}
                size="small"
              />
              <TextField
                fullWidth
                label={<span>Фамилия <span style={{ color: 'red' }}>*</span></span>}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading || codeSent}
                size="small"
              />
            </>
          )}
          <TextField
            fullWidth
            label={mode === 'register' ? <span>Пароль <span style={{ color: 'red' }}>*</span></span> : 'Пароль'}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onPaste={mode === 'register' ? (e) => e.preventDefault() : undefined}
            disabled={loading || (mode === 'register' && codeSent)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {mode === 'register' && (
            <TextField
              fullWidth
              label={<span>Подтвердите пароль <span style={{ color: 'red' }}>*</span></span>}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              disabled={loading || codeSent}
              size="small"
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Пароли не совпадают' : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {captchaRequired && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Требуется проверка CAPTCHA
              </Typography>
              <ReCaptchaWidget
                onVerify={(token) => {
                  setCaptchaToken(token);
                }}
              />
            </Box>
          )}

          {mode === 'login' ? (
            <Button variant="contained" size="large" onClick={() => handleLogin(captchaRequired && captchaToken ? { captcha_token: captchaToken } : undefined)} disabled={loading || (captchaRequired && !captchaToken)} startIcon={<EmailIcon />}>
              {loading ? 'Вход...' : 'Войти по email'}
            </Button>
          ) : (
            <>
              {mode === 'register' && password && passwordErrors.length > 0 && (
                <Alert severity="warning" sx={{ textAlign: 'left' }}>
                  <Typography variant="caption">Требования к паролю: {passwordErrors.join(', ')}</Typography>
                </Alert>
              )}
              {regError && (
                <Alert severity="error" sx={{ textAlign: 'left' }} onClose={() => setRegError('')}>
                  {regError}
                </Alert>
              )}
              {!codeSent ? (
                <Button variant="contained" size="large" onClick={handleSendRegisterCode} disabled={loading} startIcon={<EmailIcon />}>
                  {loading ? 'Отправка...' : 'Отправить проверочный код'}
                </Button>
              ) : (
                <Button variant="text" onClick={() => { setCodeSent(false); setVerificationCode(''); }} disabled={loading} size="small">
                  Изменить данные
                </Button>
              )}
              <Button variant="text" onClick={() => { setMode('login'); }} disabled={loading} size="small">
                Уже есть аккаунт? Войти
              </Button>
            </>
          )}

          {mode === 'login' && loginFailed && (
            <Button variant="text" onClick={() => setMode('recover')} disabled={loading} size="small">
              Восстановить пароль
            </Button>
          )}
        </>
      )}

      {mode === 'recover' && (
        <>
          {recoverMessage && <Alert severity="success" sx={{ textAlign: 'left' }}>{recoverMessage}</Alert>}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || recoverStep === 'code'}
            size="small"
          />
          {recoverStep === 'code' && (
            <>
              <TextField
                fullWidth
                label="Новый пароль"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                disabled={loading}
                size="small"
                helperText="Минимум 10 символов: заглавные и строчные буквы, цифры, спецсимволы"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} onMouseDown={(e) => e.preventDefault()} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Подтвердите пароль"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onPaste={(e) => e.preventDefault()}
                disabled={loading}
                size="small"
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Пароли не совпадают' : undefined}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} onMouseDown={(e) => e.preventDefault()} edge="end" size="small">
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Код из письма"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={loading}
                size="small"
                helperText="6-значный код"
              />
            </>
          )}
          {password && passwordErrors.length > 0 && (
            <Alert severity="warning" sx={{ textAlign: 'left' }}>
              <Typography variant="caption">Требования к паролю: {passwordErrors.join(', ')}</Typography>
            </Alert>
          )}
          {regError && (
            <Alert severity="error" sx={{ textAlign: 'left' }} onClose={() => setRegError('')}>
              {regError}
            </Alert>
          )}
          {recoverStep === 'email' ? (
            <Button variant="contained" size="large" onClick={handleSendRecoverCode} disabled={loading || !email} startIcon={<EmailIcon />}>
              {loading ? 'Отправка...' : 'Отправить код'}
            </Button>
          ) : (
            <Button variant="contained" size="large" onClick={handleResetPassword} disabled={loading || !verificationCode || !password} startIcon={<EmailIcon />}>
              {loading ? 'Смена...' : 'Сменить пароль'}
            </Button>
          )}
          <Button variant="text" onClick={() => { setMode('login'); }} disabled={loading} size="small">
            Назад к входу
          </Button>
        </>
      )}

      {mode === 'register' && codeSent && !codeVerified && (
        <>
          <Divider sx={{ my: 0.5 }} />
          {verifyMessage && (
            <Alert severity="info" sx={{ textAlign: 'left' }}>{verifyMessage}</Alert>
          )}
          <TextField
            fullWidth
            label="Код из письма"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            disabled={loading}
            helperText="6-значный код"
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Я ознакомился(ась) с{' '}
                <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover">
                  Политикой конфиденциальности
                </Link>{' '}
                и даю согласие на обработку персональных данных
              </Typography>
            }
            sx={{
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': { lineHeight: 1.4 },
              '& .MuiCheckbox-root': { paddingTop: '4px' },
            }}
          />
          <Button
            variant="contained"
            size="large"
            onClick={handleVerifyAndRegister}
            disabled={loading || !verificationCode || !consentGiven}
            startIcon={<EmailIcon />}
          >
            {loading ? 'Регистрация...' : 'Зарегистрироваться'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default LoginModal;
